import React from 'react';
import { motion } from 'framer-motion';
import { CubeTransparentIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';
import { mockMonteCarloData } from '../../data/teaserMockData';

interface MonteCarloSimulationDemoProps extends TeaserDemoProps {
  projections?: typeof mockMonteCarloData.projections;
  confidence?: typeof mockMonteCarloData.confidence;
}

const MonteCarloSimulationDemoComponent: React.FC<MonteCarloSimulationDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  projections = mockMonteCarloData.projections.slice(0, 3), // Show first 3 projection paths
  confidence = mockMonteCarloData.confidence
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const formatValue = (value: number) => `$${(value / 1000).toFixed(0)}k`;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 8, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity }
          }}
          className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <CubeTransparentIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Monte Carlo Engine</h4>
          <p className="text-gray-300 text-xs">10,000 simulations</p>
        </div>
      </div>

      {/* Mini Chart Visualization */}
      <div className="relative h-24 mb-4 bg-black/20 rounded-lg p-2 overflow-hidden">
        <div className="relative w-full h-full">
          {/* Starting point */}
          <div className="absolute left-0 top-1/2 w-1 h-1 bg-blue-400 rounded-full transform -translate-y-1/2" />
          
          {/* Projection paths */}
          {projections.map((_, pathIndex) => (
            <motion.svg
              key={pathIndex}
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 50"
              preserveAspectRatio="none"
            >
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ 
                  duration: 2.5, 
                  delay: pathIndex * 0.3, 
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop",
                  repeatDelay: 3
                }}
                d={`M 0 25 Q 25 ${25 - pathIndex * 3} 50 ${20 + pathIndex * 2} T 100 ${15 + pathIndex * 4}`}
                stroke={pathIndex === 0 ? '#10b981' : pathIndex === 1 ? '#f59e0b' : '#ef4444'}
                strokeWidth="0.5"
                fill="none"
                className="opacity-70"
              />
            </motion.svg>
          ))}
        </div>
      </div>

      {/* Confidence Intervals */}
      <div className="space-y-2">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="flex items-center justify-between bg-green-500/20 rounded-lg p-2 border border-green-500/30"
        >
          <span className="text-green-300 text-sm font-medium">Best Case (95%)</span>
          <motion.span
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-green-400 font-bold text-sm"
          >
            {formatValue(confidence.p95)}
          </motion.span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="flex items-center justify-between bg-blue-500/20 rounded-lg p-2 border border-blue-500/30"
        >
          <span className="text-blue-300 text-sm font-medium">Most Likely (50%)</span>
          <motion.span
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            className="text-blue-400 font-bold text-sm"
          >
            {formatValue(confidence.p50)}
          </motion.span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5 }}
          className="flex items-center justify-between bg-red-500/20 rounded-lg p-2 border border-red-500/30"
        >
          <span className="text-red-300 text-sm font-medium">Worst Case (5%)</span>
          <motion.span
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            className="text-red-400 font-bold text-sm"
          >
            {formatValue(confidence.p5)}
          </motion.span>
        </motion.div>
      </div>

      {/* Statistical Badge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="mt-3 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-purple-300 text-xs font-medium"
        >
          📊 Statistical forecasting model
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const MonteCarloSimulationDemo = createOptimizedTeaserDemo(
  MonteCarloSimulationDemoComponent,
  'MonteCarloSimulationDemo'
);